import React, { useState, useEffect, useCallback } from 'react';
import masterService from '../../services/masterService';
import MasterPageWrapper from './common/MasterPageWrapper';
import FormSection from './common/FormSection';
import ConfirmModal from './components/ConfirmModal';
import CommonPagination from '../../components/common/CommonPagination';
import usePagination from '../../hooks/usePagination';
import CascadingLocationSelector from '../../components/location/CascadingLocationSelector';

const INITIAL_CENTRE = {
  centreId: 0,
  centreCode: '',
  centreName: '',
  uhidCode: '',
  countryId: 1,
  country: 'India',
  stateId: '',
  state: '',
  cityId: '',
  city: '',
  pincode: '',
  address: '',
  mobile: '',
  email: '',
  isNabl: false,
  isActive: true
};

export const CentreMasterPage = ({ showToast }) => {
  // View Mode: 'form' | 'list'
  const [viewMode, setViewMode] = useState('form');
  const [isEditMode, setIsEditMode] = useState(false);

  // Form State
  const [formData, setFormData] = useState(INITIAL_CENTRE);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // List State
  const [centres, setCentres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [nablFilter, setNablFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Confirmation Modal
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Fetch Centres for List
  const fetchCentres = useCallback(async () => {
    setLoading(true);
    try {
      const res = await masterService.getCentres({
        search: searchQuery.trim() || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active'
      });
      if (res.success && res.data) {
        let list = res.data || [];
        if (nablFilter !== 'all') {
          const isNablBool = nablFilter === 'nabl';
          list = list.filter(c => c.isNabl === isNablBool);
        }
        setCentres(list);
      }
    } catch (err) {
      showToast?.('Failed to load diagnostic processing centres.', 'error');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, nablFilter, statusFilter, showToast]);

  useEffect(() => {
    if (viewMode === 'list') {
      fetchCentres();
    }
  }, [viewMode, fetchCentres]);

  // Handle Form Change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Add New Form
  const handleAddNew = () => {
    const randomCode = `CTR_${Math.floor(100 + Math.random() * 900)}`;
    setFormData({
      ...INITIAL_CENTRE,
      centreCode: randomCode,
      uhidCode: `${randomCode}_`
    });
    setErrors({});
    setIsEditMode(false);
    setViewMode('form');
  };

  // Edit Row
  const handleEditRow = (row) => {
    setFormData({
      centreId: row.centreId,
      centreCode: row.centreCode || '',
      centreName: row.centreName || '',
      uhidCode: row.uhidCode || '',
      address: row.address || '',
      city: row.city || '',
      state: row.state || '',
      mobile: row.mobile || '',
      email: row.email || '',
      isNabl: !!row.isNabl,
      isActive: row.isActive !== false
    });
    setErrors({});
    setIsEditMode(true);
    setViewMode('form');
  };

  // Validation
  const validate = () => {
    const errs = {};
    if (!formData.centreCode.trim()) errs.centreCode = 'Centre Code is required.';
    if (!formData.centreName.trim()) errs.centreName = 'Centre Name is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        centreCode: formData.centreCode.trim().toUpperCase(),
        centreName: formData.centreName.trim(),
        uhidCode: formData.uhidCode?.trim() || formData.centreCode.trim(),
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        mobile: formData.mobile?.trim() || null,
        email: formData.email?.trim() || null,
        isNabl: formData.isNabl,
        isActive: formData.isActive
      };

      let res;
      if (isEditMode) {
        res = await masterService.updateCentre(formData.centreId, payload);
      } else {
        res = await masterService.createCentre(payload);
      }

      if (res.success) {
        showToast?.(`Diagnostic centre ${isEditMode ? 'updated' : 'created'} successfully!`, 'success');
        setViewMode('list');
        fetchCentres();
      } else {
        showToast?.(res.message || 'Operation failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error saving diagnostic centre.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Status Toggle
  const handleConfirmStatus = async () => {
    if (!confirmTarget) return;
    setStatusUpdating(true);
    try {
      const newStatus = !confirmTarget.isActive;
      const res = await masterService.updateCentreStatus(confirmTarget.centreId, newStatus);
      if (res.success) {
        showToast?.(`Centre ${newStatus ? 'activated' : 'deactivated'}.`, 'success');
        setCentres(prev => prev.map(c => c.centreId === confirmTarget.centreId ? { ...c, isActive: newStatus } : c));
      } else {
        showToast?.(res.message || 'Status update failed.', 'error');
      }
    } catch (err) {
      showToast?.('Error updating status.', 'error');
    } finally {
      setStatusUpdating(false);
      setConfirmTarget(null);
    }
  };

  // Centralized Standardized Pagination
  const {
    page,
    pageSize,
    totalRecords,
    pagedItems: pagedCentres,
    setPage,
    setPageSize
  } = usePagination({
    items: centres,
    resetDeps: [searchQuery, nablFilter, statusFilter]
  });

  return (
    <MasterPageWrapper
      masterTitle={viewMode === 'form' ? (isEditMode ? 'Update Diagnostic Centre' : 'Register Diagnostic Centre') : 'Centre Master'}
      masterName="Centre"
      viewMode={viewMode}
      onToggleView={(mode) => {
        if (mode === 'form' && !isEditMode) handleAddNew();
        else setViewMode(mode);
      }}
    >
      {/* ─── FULL-SCREEN FORM VIEW ─── */}
      {viewMode === 'form' && (
        <div className="glass-card fade-in">
          <form onSubmit={handleSubmit} noValidate>
            {/* Section 1: Identification */}
            <FormSection
              title="01. Facility Identification"
              iconColor="#4287f5"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">
                    Centre Code <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.centreCode ? 'border-danger' : ''}`}
                    style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                    value={formData.centreCode}
                    onChange={(e) => handleChange('centreCode', e.target.value.toUpperCase())}
                    placeholder="e.g. CTR_MAIN, CTR_EAST"
                    required
                  />
                  {errors.centreCode && <span className="error-message">{errors.centreCode}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label">UHID Prefix / Serial Identifier</label>
                  <input
                    type="text"
                    className="form-input"
                    style={{ fontFamily: 'monospace' }}
                    value={formData.uhidCode}
                    onChange={(e) => handleChange('uhidCode', e.target.value)}
                    placeholder="e.g. AUR_ (Used in Patient UHID generation)"
                  />
                </div>

                <div className="form-group col-span-2">
                  <label className="form-label">
                    Diagnostic Centre Name <span className="form-req">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-input ${errors.centreName ? 'border-danger' : ''}`}
                    value={formData.centreName}
                    onChange={(e) => handleChange('centreName', e.target.value)}
                    placeholder="e.g. Main Regional Reference Lab & Processing Hub"
                    required
                  />
                  {errors.centreName && <span className="error-message">{errors.centreName}</span>}
                </div>
              </div>
            </FormSection>

            {/* Section 2: Location & Contact */}
            <FormSection
              title="02. Facility Location & Communications"
              iconColor="#10b981"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              }
            >
              <div className="form-grid">
                <div className="form-group col-span-2">
                  <CascadingLocationSelector
                    countryId={formData.countryId || 1}
                    stateId={formData.stateId}
                    cityId={formData.cityId}
                    pinCode={formData.pincode}
                    address={formData.address}
                    countryName={formData.country || 'India'}
                    stateName={formData.state}
                    cityName={formData.city}
                    onChange={(loc) => {
                      setFormData((prev) => ({
                        ...prev,
                        countryId: loc.countryId,
                        country: loc.countryName,
                        stateId: loc.stateId,
                        state: loc.stateName,
                        cityId: loc.cityId,
                        city: loc.cityName,
                        pincode: loc.pinCode,
                        address: loc.address,
                      }));
                    }}
                    layout="grid"
                    showAddress={true}
                    showPinCode={true}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Facility Mobile / Phone</label>
                  <input
                    type="tel"
                    className="form-input"
                    value={formData.mobile}
                    onChange={(e) => handleChange('mobile', e.target.value)}
                    placeholder="Helpline or front desk phone"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Official Email Address</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="lab@centre.org"
                  />
                </div>
              </div>
            </FormSection>

            {/* Section 3: Compliance & Status */}
            <FormSection
              title="03. Accreditation & Status"
              iconColor="#f59e0b"
              icon={
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="8" r="7" />
                  <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
                </svg>
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isNabl}
                    onChange={(e) => handleChange('isNabl', e.target.checked)}
                  />
                  <span><strong>NABL Accredited Facility</strong> (Enable NABL ISO 15189 stamp on reports generated from this centre)</span>
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                  />
                  <span><strong>Centre Active</strong> (Available for patient counter bookings and specimen routing)</span>
                </label>
              </div>
            </FormSection>

            {/* Form Footer */}
            <div className="form-actions-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setViewMode('list')}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ minWidth: '140px' }}
              >
                {saving ? 'Saving...' : (isEditMode ? 'Update Centre' : 'Save Centre')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── FULL-SCREEN GRID / LIST VIEW ─── */}
      {viewMode === 'list' && (
        <div className="glass-card fade-in" style={{ padding: '1.5rem' }}>
          {/* Filter Bar */}
          <div className="filter-bar">
            <div className="filter-bar-left">
              <div style={{ position: 'relative', width: '320px', minWidth: '240px' }}>
                <input
                  type="text"
                  className="form-input"
                  style={{ paddingLeft: '2.4rem' }}
                  placeholder="Search code, centre name, city..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>

              <select
                className="form-select"
                style={{ width: '160px' }}
                value={nablFilter}
                onChange={(e) => { setNablFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Accreditation</option>
                <option value="nabl">NABL Certified Only</option>
                <option value="standard">Standard Lab</option>
              </select>

              <select
                className="form-select"
                style={{ width: '140px' }}
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="inactive">Inactive Only</option>
              </select>
            </div>

            <div className="filter-bar-right">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddNew}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>+ Add Diagnostic Centre</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="master-table mvc-table">
              <thead>
                <tr>
                  <th style={{ width: '60px', textAlign: 'center' }}>Sr.</th>
                  <th style={{ width: '120px' }}>Centre Code</th>
                  <th>Diagnostic Centre Name</th>
                  <th style={{ width: '130px' }}>UHID Prefix</th>
                  <th style={{ width: '140px' }}>Location</th>
                  <th style={{ width: '130px', textAlign: 'center' }}>Accreditation</th>
                  <th style={{ width: '140px' }}>Contact</th>
                  <th style={{ width: '100px', textAlign: 'center' }}>Status</th>
                  <th style={{ width: '140px', textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      Loading diagnostic centres catalog...
                    </td>
                  </tr>
                ) : centres.length === 0 ? (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
                      No centres matching your criteria.
                    </td>
                  </tr>
                ) : (
                  pagedCentres.map((r, index) => (
                    <tr key={r.centreId || index}>
                      <td style={{ textAlign: 'center', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                        {(page - 1) * pageSize + index + 1}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--accent)' }}>
                        {r.centreCode}
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{r.centreName}</span>
                          {r.address && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{r.address}</span>}
                        </div>
                      </td>
                      <td style={{ fontFamily: 'monospace', color: 'var(--accent)' }}>
                        {r.uhidCode || '—'}
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {r.city ? `${r.city}${r.state ? `, ${r.state}` : ''}` : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {r.isNabl ? (
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#047857', backgroundColor: '#ecfdf5', padding: '2px 6px', borderRadius: '4px', border: '1px solid #a7f3d0' }}>
                            ✓ NABL
                          </span>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Standard</span>
                        )}
                      </td>
                      <td style={{ fontSize: '0.825rem', fontFamily: 'monospace' }}>
                        {r.mobile || r.email || '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span className={`badge-status ${r.isActive ? 'active' : 'inactive'}`}>
                          {r.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{ padding: '3px 8px', fontSize: '11.5px' }}
                            onClick={() => handleEditRow(r)}
                            title="Edit Centre"
                          >
                            Edit
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            style={{
                              padding: '3px 8px',
                              fontSize: '11.5px',
                              color: r.isActive ? '#ef4444' : '#10b981'
                            }}
                            onClick={() => setConfirmTarget(r)}
                            title={r.isActive ? 'Deactivate Centre' : 'Activate Centre'}
                          >
                            {r.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Standardized LIMS Centralized Pagination */}
          <CommonPagination
            page={page}
            pageSize={pageSize}
            totalRecords={totalRecords}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            loading={loading}
          />
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={!!confirmTarget}
        title={confirmTarget?.isActive ? 'Deactivate Centre' : 'Activate Centre'}
        message={
          confirmTarget?.isActive
            ? `Are you sure you want to deactivate "${confirmTarget?.centreName}"?`
            : `Are you sure you want to activate "${confirmTarget?.centreName}"?`
        }
        confirmLabel={confirmTarget?.isActive ? 'Deactivate' : 'Activate'}
        variant={confirmTarget?.isActive ? 'danger' : 'primary'}
        loading={statusUpdating}
        onConfirm={handleConfirmStatus}
        onCancel={() => setConfirmTarget(null)}
      />
    </MasterPageWrapper>
  );
};

export default CentreMasterPage;
